import React from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import ModalSimple from '../../global/modal-simple';
import InfoIcon from './shared/info-icon';
import {currency, getPhoneHREF, modal, openLink, Portal} from '../../util';

interface IDocSize {
    i: number;
    w: string;
    h: string;
}

const doc_sizes: IDocSize[] = [
    {i: 0, h: '6.0', w: '8.0'},
    {i: 1, h: '7.0', w: '9.0'},
    {i: 2, h: '8.0', w: '10.0'},
    {i: 3, h: '8.5', w: '11.0'},
    {i: 4, h: '10.0', w: '12.0'},
    {i: 5, h: '11.0', w: '14.0'},
    {i: 6, h: '12.0', w: '16.0'},
    {i: 7, h: '13.0', w: '17.0'},
    {i: 8, h: '14.0', w: '18.0'},
    {i: 9, h: '16.0', w: '20.0'},
    {i: 10, h: '18.0', w: '22.0'},
    {i: 11, h: '20.0', w: '24.0'},
];

const doc_sizes_photo: IDocSize[] = [
    {i: 0, h: '5.0', w: '7.0'},
    {i: 1, h: '8.0', w: '10.0'},
];

const doc_sizes_varsity: IDocSize[] = [
    {i: 0, h: '8.0', w: '9.25'},
    {i: 1, h: '10.0', w: '12.0'},
];

const DocSize: React.FC = observer(() => {
    const changeDocSize = (e: React.ChangeEvent<HTMLSelectElement>) => {
        e.preventDefault();
        let dw: IDocSize;
        if (s.prod.DYF_FrameStyleID === 10) {
            dw = doc_sizes_photo[parseInt(e.target.value, 10)];
        } else if (s.prod.DYF_FrameStyleID === 4) {
            dw = doc_sizes_varsity[parseInt(e.target.value, 10)];
        } else {
            dw = doc_sizes[parseInt(e.target.value, 10)];
        }
        const _w: number = parseFloat(dw.w);
        const _h: number = parseFloat(dw.h);

        s.ps.docWidth = _w;
        s.ps.docHeight = _h;
        if (s.ps.is_horizontal && s.ps.docWidth < s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        } else if (!s.ps.is_horizontal && s.ps.docWidth > s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        }
        s.ps.docWidth2 = s.ps.docWidth;
        s.ps.docHeight2 = s.ps.docHeight;

        if (s.prod.DYF_FrameStyleID !== 2) {
            s.ps.docWidth2 = -1;
            s.ps.docHeight2 = -1;
        }

        if (s.prod.IsCAF) {
            if (s.prod.DYF_FrameStyleID === 2 || s.prod.DYF_FrameStyleID === 7 || s.prod.DYF_FrameStyleID === 8 || s.prod.DYF_FrameStyleID === 6) {
                if (s.ps.docWidth > 9 && s.ps.docHeight > 12) {
                    s.ps.use_plexi = true;
                } else if (s.ps.docWidth > 12 && s.ps.docHeight > 9) {
                    s.ps.use_plexi = true;
                } else {
                    s.ps.use_plexi = false;
                }
            } else if (s.prod.DYF_FrameStyleID === 3 || s.prod.DYF_FrameStyleID === 5) {
                if (s.ps.docWidth > 11 && s.ps.docHeight > 14) {
                    s.ps.use_plexi = true;
                } else if (s.ps.docWidth > 14 && s.ps.docHeight > 11) {
                    s.ps.use_plexi = true;
                } else {
                    s.ps.use_plexi = false;
                }
            } else {
                if (s.ps.docWidth > 14 && s.ps.docHeight > 18) {
                    s.ps.use_plexi = true;
                } else if (s.ps.docWidth > 18 && s.ps.docHeight > 14) {
                    s.ps.use_plexi = true;
                } else {
                    s.ps.use_plexi = false;
                }
            }
        }

        if (s.prod.ForcePlexiglass) {
            s.ps.use_plexi = true;
        }

        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    };

    const changeOrientation = (e: React.ChangeEvent<HTMLSelectElement>) => {
        s.ps.is_horizontal = !s.ps.is_horizontal;
        s.prod.IsHorizontal = s.ps.is_horizontal;
        s.prod.OrientationHorizontal = s.ps.is_horizontal;
        if (s.ps.is_horizontal && s.ps.docWidth < s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        } else if (!s.ps.is_horizontal && s.ps.docWidth > s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        }
        s.ps.docWidth2 = s.ps.docWidth;
        s.ps.docHeight2 = s.ps.docHeight;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    };

    const changeDYF = (e: React.ChangeEvent<HTMLSelectElement>) => {
        openLink(e.target.value);
    };

    if (!s.ps.is_caf) {
        if (s.ps.docWidth <= 0) {
            return null;
        }

        return (
            <div id="doc-size">
                <ModalSimple
                    id="modal-doc-size"
                    maxWidth="800px"
                    title={`${s.ui.doc_type} Size Information`}
                >
                    <picture>
                        <source
                            type="image/webp"
                            srcSet={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.webp`}
                        />
                        <img
                            src={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.png`}
                            alt="Document Size Information"
                            aria-label="Document size refers to the dimensions of the entire document"
                            style={{width: '100%'}}
                        />
                    </picture>
                </ModalSimple>
                <ModalSimple
                    id="modal-doc-size-diff"
                    title={`Need a frame matching a different ${s.ui.doc_type.toLowerCase()} size?`}
                >
                    <div>
                        <p>All of our frames are custom made to order so we can very easily create a frame that fits your specific Document size.</p>
                        <p>
                            Please call our customer service department at <a href={getPhoneHREF()}>{Portal?.CustomerServicePhone}</a> or email <a href={`mailto:${Portal?.CustomerServiceEmail}`}>{Portal?.CustomerServiceEmail}</a> and provide the Item # of the frame you would like to purchase along
                            with any customizations and the size and orientation of your Document.
                        </p>
                        <p>We will provide a price and answer any questions you may have about this custom frame.</p>
                    </div>
                </ModalSimple>
                <div className="prod-doc-size-display">
                    <div>
                        <span
                            className="header-std"
                            style={{
                                paddingRight: '0.25rem',
                            }}
                        >
                            {s.ui.doc_type} Size:{' '}
                        </span>
                        {s.ps.docWidth}"w x {s.ps.docHeight}"h{' '}
                        <InfoIcon
                            id="modal-doc-size"
                            ariaLabel="View information about document size for this product"
                        />
                    </div>
                    <div>
                        <button
                            className="link link-btn"
                            onClick={() => modal('modal-doc-size-diff')}
                            type="button"
                            aria-label="View information about different document sizes"
                            aria-haspopup="dialog"
                            aria-controls="modal-doc-size-diff"
                        >
                            Different Size?
                        </button>
                    </div>
                    {s.si != null && s.si.Price_Retail != null && !s.isRetail && <div>Retail: {currency(s.si.Price_Retail)}</div>}
                </div>
            </div>
        );
    } else {
        return (
            <div
                className="gray-box caf-doc mt-2"
                style={{marginBottom: '0.5rem'}}
            >
                <ModalSimple
                    id="modal-doc-size-caf"
                    title={"Don't See Your Size?"}
                >
                    <div>
                        <p>No worries! We'll ask for the exact size once you Add To Cart. In the meantime, we'll use the 'Up to' size to price your frame.</p>
                        <p>All of our frames are custom made to order so we can very easily create a frame that fits your specific Document size.</p>
                    </div>
                </ModalSimple>
                <div
                    className="row"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <div className="col-12 col-sm-6">
                        <div style={{fontSize: '15px', fontWeight: 700}}>
                            {s.prod.DocType} Size &amp; Orientation{' '}
                            <InfoIcon
                                id="modal-doc-size-caf"
                                ariaLabel="View information about document size for this product"
                            />
                        </div>
                        <select
                            className="cust-entity"
                            aria-label="Select document size"
                            onChange={changeDocSize}
                            style={{width: 230, display: 'block'}}
                        >
                            {s.prod.DYF_FrameStyleID !== 10 &&
                                s.prod.DYF_FrameStyleID !== 4 &&
                                doc_sizes.map((m, i) => (
                                    <option
                                        key={i}
                                        selected={m.i === 3}
                                        value={m.i}
                                    >
                                        {s.ps.is_horizontal ? `Up to ${m.w}"w x ${m.h}"h` : `Up to ${m.h}"w x ${m.w}"h`}
                                    </option>
                                ))}
                            {s.prod.DYF_FrameStyleID === 10 &&
                                doc_sizes_photo.map((m, i) => (
                                    <option
                                        key={i}
                                        selected={m.i === 0}
                                        value={m.i}
                                    >
                                        {s.ps.is_horizontal ? `Up to ${m.w}"w x ${m.h}"h` : `Up to ${m.h}"w x ${m.w}"h`}
                                    </option>
                                ))}
                            {s.prod.DYF_FrameStyleID === 4 &&
                                doc_sizes_varsity.map((m, i) => (
                                    <option
                                        key={i}
                                        selected={m.i === 0}
                                        value={m.i}
                                    >
                                        {s.ps.is_horizontal ? `Up to ${m.w}"w x ${m.h}"h` : `Up to ${m.h}"w x ${m.w}"h`}
                                    </option>
                                ))}
                        </select>
                        <select
                            className="cust-entity"
                            aria-label="Select document orientation"
                            onChange={changeOrientation}
                            style={{width: 230, display: 'block'}}
                        >
                            <option
                                value={'horizontal'}
                                selected={s.ps.is_horizontal}
                            >
                                Horizontal
                            </option>
                            <option
                                value={'vertical'}
                                selected={!s.ps.is_horizontal}
                            >
                                Vertical
                            </option>
                        </select>
                    </div>
                    {s.prod && s.prod.dyfList && (
                        <div className="col-12 col-sm-6">
                            <div style={{fontSize: '15px', fontWeight: 700}}>Frame Style</div>
                            <select
                                className="cust-entity"
                                aria-label="Select frame style"
                                onChange={changeDYF}
                                style={{maxWidth: 230, width: 230}}
                            >
                                {s.prod.dyfList.map((m, i) => (
                                    <option
                                        key={i}
                                        value={m.id}
                                        selected={m.i === s.prod.ProductID}
                                    >
                                        {m.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>
        );
    }
});

export default DocSize;
